import { computed, inject, Injectable } from '@angular/core';
import { AmenityChange, FilterCriteriaChanges, PriceRangeChange } from '@rx-applications/clientsite/search/shared';
import { AmenityName, localFiltersDefaultCookieName } from '@rx-applications/clientsite/shared/utils';
import { CookieService } from 'ngx-cookie-service';
import { CheckboxChangeEvent } from '@roomex/rx-components';
import { SearchBoxEvent } from '@rx-applications/clientsite/search/searchbox/data-access';
import { FilterFacade } from '@rx-applications/clientsite/search/filter/data-access';

@Injectable({ providedIn: 'root' })
export class LocalFiltersService {
  private cookieService = inject(CookieService);
  private filterFacade = inject(FilterFacade);

  hasLocalFilters = computed(() => !!this.cookieService.get(localFiltersDefaultCookieName))

  updateAmenityLocalFilters(newAmenity: { value: AmenityName; checked: boolean }): void {
    this.updateLocalFilters((filters) => {
      const amenityName: AmenityName = newAmenity.value;
      const checked = newAmenity.checked;
      const savedAmenities: AmenityChange[] = filters.amenities || [];
      const index = savedAmenities.findIndex((item) => item.amenity === amenityName);

      if (index !== -1) {
        savedAmenities[index].checked = checked;
      } else {
        savedAmenities.push({ amenity: amenityName, checked });
      }

      return { amenities: savedAmenities };
    });
  }

  updatePropertyTypeLocalFilters(newPropertyType: { value: string; checked: boolean }): void {
    this.updateLocalFilters((filters) => {
      const savedPropertyTypes = filters.propertyType || [];
      const index = savedPropertyTypes.findIndex((item) => item.value === newPropertyType.value);

      if (index !== -1) {
        savedPropertyTypes[index].checked = newPropertyType.checked;
      } else {
        savedPropertyTypes.push({
          value: newPropertyType.value,
          checked: newPropertyType.checked,
        });
      }

      return { propertyType: savedPropertyTypes };
    });
  }

  updatePriceRangeLocalFilter(change: PriceRangeChange): void {
    this.updateLocalFilters((filters) => {
      const currentRange = filters.priceRange?.range || [0, 0];
      const newRange: [number, number] = [...currentRange];

      if (change.endpoint === 'min') {
        newRange[0] = change.range[0];
      } else {
        newRange[1] = change.range[1];
      }

      return {
        priceRange: {
          endpoint: change.endpoint,
          range: newRange,
        },
      };
    });
  }

  updateStarsLocalFilters(stars: number[]): void {
    this.updateLocalFilters(() => ({ stars }));
  }

  updateMealOptionLocalFilter(mealOption: { value: number; checked: boolean }): void {
    this.updateLocalFilters(() => {
      const updatedMeal = mealOption.checked ? mealOption.value : undefined;
      return { meal: updatedMeal };
    });
  }

  private updateLocalFilters(
    updateFn: (currentFilters: Partial<FilterCriteriaChanges>) =>
      | Partial<FilterCriteriaChanges>
      | {
          meal: number;
        },
  ): void {
    const currentFilters = JSON.parse(this.cookieService.get(localFiltersDefaultCookieName) || '{}');
    const updatedFilters = updateFn(currentFilters);

    const filtersToSave = {
      ...currentFilters,
      ...updatedFilters,
    };

    this.cookieService.set(localFiltersDefaultCookieName, JSON.stringify(filtersToSave), 365, '/');
  }

  saveLocalFiltersFilterBar(filterChanges: Partial<FilterCriteriaChanges>): void {
    const filtersSaved: Partial<FilterCriteriaChanges> | null = JSON.parse(
      this.cookieService.get(localFiltersDefaultCookieName) || '{}',
    );
    const filtersToSave: Partial<FilterCriteriaChanges> = {
      ...filtersSaved,
      ...filterChanges,
      amenities: this.mergeAmenities(filtersSaved.amenities || [], filterChanges.amenities || []),
      propertyType: this.mergeCheckboxes(filtersSaved.propertyType || [], filterChanges.propertyType || []),
      priceRange: this.updatePriceRange(filtersSaved.priceRange, filterChanges.priceRange),
    };

    this.cookieService.set(localFiltersDefaultCookieName, JSON.stringify(filtersToSave), 365, '/');
  }

  private mergeAmenities(saved: AmenityChange[], incoming: AmenityChange[]): AmenityChange[] {
    const map = new Map(saved.map((amenityChange) => [amenityChange.amenity, amenityChange]));
    incoming.forEach((amenityChange) => map.set(amenityChange.amenity, amenityChange));
    return Array.from(map.values());
  }

  // Generic checkbox (used for propery types)
  private mergeCheckboxes(
    saved: Omit<CheckboxChangeEvent, 'event'>[],
    incoming: Omit<CheckboxChangeEvent, 'event'>[],
  ): Omit<CheckboxChangeEvent, 'event'>[] {
    const map = new Map(saved.map((checkbox) => [checkbox.value, checkbox]));
    incoming.forEach((checkbox) => map.set(checkbox.value, checkbox));
    return Array.from(map.values());
  }

  private updatePriceRange(saved?: PriceRangeChange, incoming?: PriceRangeChange): PriceRangeChange | undefined {
    // This function is very simple since we just take the incoming value or fall back to the saved one
    return incoming || saved;
  }

  applyLocalDefaultFilters(searchBoxEvent: SearchBoxEvent): SearchBoxEvent {
    const filtersSaved = JSON.parse(this.cookieService.get('defaultLocalFilters') || '{}');
    if (Object.keys(filtersSaved).length > 0) {
      this.filterFacade.setFilters(filtersSaved);
      if (filtersSaved.stars) {
        searchBoxEvent.queryParams['stars'] = filtersSaved.stars.join(',');
      }
      if (filtersSaved.meal) {
        this.filterFacade.setMealOptionFilter(filtersSaved.meal);
        searchBoxEvent.queryParams['meal'] = filtersSaved.meal || 0;
      }
      if (filtersSaved.amenities.some((amenity: AmenityChange) => amenity.amenity === 'loyaltyProgram')) {
        const loyaltyAmenity: AmenityChange = filtersSaved.amenities.find((amenity: AmenityChange) => amenity.amenity === 'loyaltyProgram');
        searchBoxEvent.queryParams['loyaltyguaranteed'] = loyaltyAmenity.checked;
      }
    }
    return searchBoxEvent;
  }

  clearLocalFilters() {
    this.cookieService.delete(localFiltersDefaultCookieName, '/');
  }
}
